import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

/**
 * Related Products Component
 * Lazy loads product recommendations when section enters viewport
 * Dispatches gridUpdated event to initialize lazy loading and other components
 */
class RelatedProducts {
  constructor(section) {
    this.section = section;

    if (!this.section.dataset.url) {
      return;
    }

    this.init();
  }

  init() {
    const observer = new IntersectionObserver(
      (entries, obs) => this.handleIntersection(entries, obs),
      { rootMargin: '0px 0px 200px 0px' }
    );

    observer.observe(this.section);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) {
      return;
    }

    observer.unobserve(this.section);
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const url = this.section.dataset.url;

    try {
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('.product-recommendations');

      const hasProducts = recommendations && recommendations.querySelector('[data-product-id]');

      if (hasProducts) {
        this.section.innerHTML = recommendations.innerHTML;
        this.section.removeAttribute('style');
        
        ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));

        this.initSliders();
        this.initRatingSnippets();
      }
    } catch (error) {
      // Section remains hidden
    }
  }

  /**
   * Initializes sliders within this section with full controls support.
   * Reads configuration from data-swiper-options and wires up pagination
   * and navigation from slider-controls elements.
   */
  initSliders() {
    const sliderBlocks = this.section.querySelectorAll('[data-slider-block]');
    if (!sliderBlocks.length) return;

    const Swiper = window.Swiper;
    if (!Swiper) return;

    sliderBlocks.forEach(block => {
      const contentEl = block.querySelector('.slider-content');
      if (!contentEl) return;

      const controlsElement = block.querySelector('[data-slider-control]');

      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      const slides = [...contentEl.children].filter(el =>
        !excludedTags.includes(el.tagName) &&
        !el.hasAttribute('data-slider-control')
      );

      if (slides.length < 1) return;

      const swiperEl = document.createElement('div');
      swiperEl.classList.add('swiper', 'slider-block-swiper', 'w-full', 'max-w-full');

      const wrapperEl = document.createElement('div');
      wrapperEl.classList.add('swiper-wrapper');

      slides.forEach(slide => {
        slide.classList.add('swiper-slide');
        wrapperEl.appendChild(slide);
      });

      swiperEl.appendChild(wrapperEl);
      contentEl.innerHTML = '';
      contentEl.appendChild(swiperEl);

      const customOptions = block.dataset.swiperOptions
        ? JSON.parse(block.dataset.swiperOptions)
        : {};

      const hasLoopEnabled = customOptions.loop === true;
      const hasAutoplayEnabled = Boolean(customOptions.autoplay);
      const shouldUseEdgeNavigationStyles = Boolean(controlsElement) && !hasLoopEnabled && !hasAutoplayEnabled;

      if (controlsElement) {
        controlsElement.classList.toggle('slider-nav-edge-aware', shouldUseEdgeNavigationStyles);

        const paginationEl = controlsElement.querySelector('.swiper-pagination');
        if (paginationEl) {
          const paginationType = paginationEl.dataset.paginationType || 'bullets';
          customOptions.pagination = {
            el: paginationEl,
            type: paginationType,
            clickable: true,
          };
        }

        const nextBtn = controlsElement.querySelector('.slider-navigation-next');
        const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
        if (nextBtn && prevBtn) {
          customOptions.navigation = {
            nextEl: nextBtn,
            prevEl: prevBtn,
          };
        }
      }

      if (shouldUseEdgeNavigationStyles) {
        customOptions.loop = false;
      }

      new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 16,
        grabCursor: true,
        allowTouchMove: true,
        simulateTouch: true,
        mousewheel: {
          enabled: true,
          forceToAxis: true,
        },
        freeMode: {
          enabled: true,
          sticky: true,
        },
        ...customOptions,
      });
    });
  }

  /**
   * Re-triggers Reviews.io rating snippet initialization for dynamically
   * injected product cards that were not present at initial page load.
   */
  initRatingSnippets() {
    if (typeof window.ratingSnippet === 'function' && window.ratingSnippetGlobalSettings) {
      const settings = window.ratingSnippetGlobalSettings;
      window.ratingSnippet(
        settings.class || 'ruk_rating_snippet',
        settings.options || settings,
      );
    }
  }

}

export async function initRelatedProducts() {
  const sections = document.querySelectorAll('.product-recommendations');
  sections.forEach(section => new RelatedProducts(section));
}

// Auto-initialize when module loads
initRelatedProducts();
